import { ActionIcon, Loader } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './voicePreviewBubble.module.css';

interface Props {
  text: string;
  isRecording: boolean;
  isSending: boolean;
  clear: () => void;
  color?: string;
}

export const VoicePreviewBubble = ({ clear, isRecording, isSending, text, color }: Props) => {
  const { t } = useTranslation();
  const mainColor = color || '#8B5CF6'; // 默认紫色

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startVisualization = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      analyser.fftSize = 256;
      source.connect(analyser);

      sourceRef.current = source;
      analyserRef.current = analyser;
      animate();
    } catch (err) {
      console.log('Please allow microphone access to use the visualizer.');
    }
  };

  const animate = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;

    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = 2;
      const gap = 2;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;

        ctx.fillStyle = mainColor;

        const y = (canvas.height - barHeight) / 2;
        ctx.fillRect(x, y, barWidth, barHeight);

        x += barWidth + gap;

        if (x >= canvas.width) break;
      }
    };

    draw();
  };

  const stopVisualization = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (isRecording) {
      startVisualization();
    } else {
      stopVisualization();
    }
  }, [isRecording]);

  useEffect(() => {
    return () => {
      stopVisualization();
    };
  }, []);

  // 生成带透明度的主色
  const shadowColor =
    mainColor.startsWith('#') && mainColor.length === 7
      ? mainColor + '4D' // 30% 透明度
      : mainColor;

  return (
    <div
      className={cx(
        classes['bubbleContainer'],
        'absolute bottom-full left-0 right-0 mb-6 bg-white border rounded-2xl p-4 w-full sm:w-[65%] mx-auto pb-[60px]',
        isRecording && classes['visible']
      )}
      style={
        {
          '--bubble-shadow': shadowColor,
        } as React.CSSProperties
      }
    >
      <span className={`${text ? 'text-gray-800' : 'text-gray-400'} text-sm mr-2 break-words`}>
        {text ? text : t('widget.recognizing')}
      </span>
      {text && <Loader size="xs" color={mainColor} type="dots" className="inline-flex" />}

      {/* 底部工具区域 */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150px] h-[40px] py-3">
        <canvas ref={canvasRef} className="w-full h-full" />
      </div>
      <ActionIcon variant="subtle" className="absolute right-2 bottom-2" onClick={clear} color={mainColor}>
        <IconX size={16} />
      </ActionIcon>
    </div>
  );
};
